import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import type { Account } from '@fatigue/shared';
import { getAccount, setContactPhone } from '../../data/api';

/**
 * The organisation itself: its name, and the number its patients are told to
 * call.
 *
 * That number is printed beneath every patient's comment box as the place to
 * ring for help before their next session, so it is the one setting here with
 * a direct patient-safety consequence. It is shown prominently, and an account
 * without one says so in as many words rather than leaving a blank — patients
 * of such an account are offered NHS 111 alone.
 */
export function Organisation(): JSX.Element {
  const [account, setAccount] = useState<Account | null>(null);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAccount()
      .then((loaded) => {
        setAccount(loaded);
        // No number yet: open straight into editing, because that is the
        // thing to do here.
        if (loaded.contactPhone === undefined) setEditing(true);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not load the organisation.');
      });
  }, []);

  const save = (event: React.FormEvent): void => {
    event.preventDefault();
    if (saving || draft.trim() === '') return;
    setSaving(true);
    setError(null);
    setContactPhone(draft)
      .then((updated) => {
        setAccount(updated);
        setEditing(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not save that number.');
      })
      .finally(() => setSaving(false));
  };

  if (account === null) {
    return error === null ? (
      <p className="admin__note">Loading…</p>
    ) : (
      <p className="admin__error" role="alert">
        {error}
      </p>
    );
  }

  return (
    <section className="admin__section">
      <header className="admin__head">
        <div>
          <h2 className="admin__title">{account.name}</h2>
          <p className="admin__sub">
            Patients see this number beneath their comment box, as the place to call for help
            before their next session.
          </p>
        </div>
      </header>

      {error !== null && (
        <p className="admin__error" role="alert">
          {error}
        </p>
      )}

      {editing ? (
        <form className="admin__inline" onSubmit={save}>
          <label className="visually-hidden" htmlFor="contact-phone">
            Phone number for patients
          </label>
          <input
            id="contact-phone"
            className="invite__input"
            type="tel"
            autoComplete="tel"
            value={draft}
            placeholder="01386 240189"
            onChange={(event) => setDraft(event.target.value)}
            disabled={saving}
          />
          <button type="submit" className="primary" disabled={saving || draft.trim() === ''}>
            {saving ? 'Saving…' : 'Save number'}
          </button>
          {account.contactPhone !== undefined && (
            <button
              type="button"
              className="ghost"
              disabled={saving}
              onClick={() => setEditing(false)}
            >
              Cancel
            </button>
          )}
        </form>
      ) : (
        <p className="org__phone">
          <span className="org__label">Phone number for patients</span>
          <strong>{account.contactPhone}</strong>
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setDraft(account.contactPhone ?? '');
              setEditing(true);
            }}
          >
            Change
          </button>
        </p>
      )}

      {account.contactPhone === undefined && (
        <p className="cohort__warning">
          No number set. Until there is one, patients are told to call NHS 111 if they need help
          before their next session.
        </p>
      )}
    </section>
  );
}
